import { useResilientGraphQLSubscription } from '~/hooks/useResilientGraphQLSubscription';
import {
  COMMUNITY_POSTS_SINCE_LAST_VISIT_QUERY,
  COMMUNITY_POST_ADDED_SUBSCRIPTION,
  MESSAGE_ADDED_SUBSCRIPTION,
  MESSAGE_UPDATED_SUBSCRIPTION,
  MESSAGE_QUERY,
  MESSAGES_QUERY,
  MY_COMMUNITIES_QUERY,
} from '~/graphql/queries';
import { useAuth } from '~/context/auth';
import { refetchInboxMessages } from '@arthelper/shared/inbox-messages-cache';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';

interface MessagePartItem {
  id: string;
  content: string;
  createdAt: string;
  senderUserId?: string | null;
  status?: string | null;
  sender: unknown;
}

interface MessageAddedData {
  messageAdded: {
    messageId: string;
    unreadDmCount: number;
    isAuthoredByViewer: boolean;
    messagePart: MessagePartItem;
  };
}

interface MessageQueryData {
  message: {
    id: string;
    parts?: {
      items?: MessagePartItem[];
      total?: number;
    };
    lastMessagePart?: MessagePartItem | null;
    updatedAt?: string;
  } | null;
}

interface CommunityPostAddedData {
  communityPostAdded: {
    postId: string;
    communityId: string;
    communitySlug: string;
    authorUserId: string;
    isAuthoredByViewer: boolean;
  };
}

interface MyCommunitiesData {
  user?: { asfId: string } | null;
  myCommunities?: Array<{
    id: string;
    name: string;
    slug: string;
    avatarUrl?: string | null;
    hasUnseenPosts?: boolean | null;
  }> | null;
}

/**
 * Live GraphQL subscriptions for DMs and community posts. Mounted once in the
 * dashboard shell; returns null. Socket client lives in graphql/client.ts —
 * this only opens operations on that existing split-link ws.
 */
export function LiveUpdatesListener() {
  const { user } = useAuth();
  const { enabled: connectionsEnabled } = useFeatureFlag('connections_and_dms');

  useResilientGraphQLSubscription<MessageAddedData>(MESSAGE_ADDED_SUBSCRIPTION, {
    skip: !user || !connectionsEnabled,
    label: 'messageAdded',
    onData: ({ client, data }) => {
      const payload = data.data?.messageAdded;
      if (!payload?.messageId || !payload.messagePart) return;

      const { messageId, messagePart, unreadDmCount } = payload;

      client.cache.updateQuery<MessageQueryData>({ query: MESSAGE_QUERY, variables: { id: messageId } }, (prev) => {
        if (!prev?.message) return prev;
        const items = prev.message.parts?.items ?? [];
        if (items.some((part) => part.id === messagePart.id)) return prev;
        const total = prev.message.parts?.total ?? items.length;
        // MESSAGE_QUERY asks for the NEWEST page (fromEnd: true), so a live part always
        // belongs at the end of this window — the old guard refused to append past the
        // window's size and froze long threads on their opening messages.
        return {
          ...prev,
          message: {
            ...prev.message,
            parts: {
              ...prev.message.parts,
              items: [...items, messagePart],
              total: total + 1,
            },
            lastMessagePart: messagePart,
            updatedAt: messagePart.createdAt,
          },
        };
      });

      client.cache.modify({
        fields: {
          unreadDmCount() {
            return unreadDmCount;
          },
        },
      });

      refetchInboxMessages(client, MESSAGES_QUERY);
    },
  });

  useResilientGraphQLSubscription<{
    messageUpdated: { messageId?: string; partId?: string | null; reactions?: unknown[] | null };
  }>(MESSAGE_UPDATED_SUBSCRIPTION, {
    skip: !user || !connectionsEnabled,
    label: 'messageUpdated',
    onData: ({ client, data }) => {
      const payload = data.data?.messageUpdated;
      // A translation finishing publishes partId and leaves reactions unset.
      // Reaction updates carry the new counts; those stay on their own path.
      if (!payload?.messageId || !payload.partId || payload.reactions != null) return;
      void client.query({
        query: MESSAGE_QUERY,
        variables: { id: payload.messageId },
        fetchPolicy: 'network-only',
      });
    },
  });

  useResilientGraphQLSubscription<CommunityPostAddedData>(COMMUNITY_POST_ADDED_SUBSCRIPTION, {
    skip: !user,
    label: 'communityPostAdded',
    onData: ({ client, data }) => {
      const payload = data.data?.communityPostAdded;
      if (!payload || payload.isAuthoredByViewer) return;

      void client.refetchQueries({ include: [COMMUNITY_POSTS_SINCE_LAST_VISIT_QUERY] });

      client.cache.updateQuery<MyCommunitiesData>({ query: MY_COMMUNITIES_QUERY }, (prev) => {
        if (!prev?.myCommunities) return prev;
        let changed = false;
        const myCommunities = prev.myCommunities.map((community) => {
          if (String(community.id) !== String(payload.communityId) || community.hasUnseenPosts) {
            return community;
          }
          changed = true;
          return { ...community, hasUnseenPosts: true };
        });
        if (!changed) return prev;
        return { ...prev, myCommunities };
      });
    },
  });

  return null;
}
